import React, { useState, memo } from 'react';
import PageHeader from '../components/layout/PageHeader';
import Tabs from '../components/ui/Tabs';
import Card from '../components/ui/Card';
import Button from '../components/ui/Button';
import { Accordion, AccordionItem } from '../components/ui/Accordion';
import { CalendarIcon, ClockIcon, MapPinIcon, UserIcon, MailIcon, PhoneIcon, BriefcaseIcon, GraduationCapIcon, HeartIcon, BuildingIcon, BookOpenIcon, AwardIcon, CheckCircleIcon, VideoIcon } from 'lucide-react';
const Anniversary = () => {
  const [formStatus, setFormStatus] = useState<'idle' | 'submitting' | 'success'>('idle');
  const handleRegistrationSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setFormStatus('submitting');
    setTimeout(() => {
      setFormStatus('success');
    }, 1500);
  };
  const tabs = [{
    id: 'overview',
    label: 'Anniversary Overview',
    content: <div className="py-8 animate-fadeIn" data-id="element-3485">
          {/* Anniversary Banners */}
          <div className="mb-12 rounded-xl overflow-hidden shadow-lg" data-id="element-3486">
            <img src="https://cdn.magicpatterns.com/uploads/uQsM27rbNPREQmqUu3VS3W/IGS_70.jpg" alt="IGS @ 70 — Celebrating 70 Years of Impactful and Enduring Legacy in Education. 23rd March 1956 – 23rd March 2026." className="w-full h-auto" data-id="element-3487" />
          </div>

          {/* Countdown Video */}
          <div className="mb-12 rounded-xl overflow-hidden shadow-lg bg-blue-950 aspect-video max-w-4xl mx-auto" data-id="element-3488">
            <video className="w-full h-full object-contain" controls autoPlay muted loop playsInline data-id="element-3489">
              <source src="https://cdn.magicpatterns.com/uploads/kSLnaCRrBAhuVk89iDgBsf/countdown.mp4" type="video/mp4" data-id="element-3490" />
              Your browser does not support the video tag.
            </video>
          </div>

          <div className="text-center mb-12" data-id="element-3491">
            <h2 className="text-3xl font-bold text-blue-900 mb-2" data-id="element-3492">
              Celebrating 70 Years of Impactful & Enduring Legacy in Education
            </h2>
            <p className="text-xl text-yellow-600 font-semibold italic mb-4" data-id="element-3493">
              Still Soaring at Seventy
            </p>
            <div className="w-24 h-1 bg-yellow-500 mx-auto mb-6" data-id="element-3494"></div>
            <p className="text-lg text-gray-700 max-w-3xl mx-auto leading-relaxed mb-4" data-id="element-3495">
              Since its establishment on March 23, 1956, Isonyin Grammar School
              has been a beacon of academic excellence, character development,
              and leadership training. As we celebrate our 70th Founder's Day
              Anniversary, we reflect on seven decades of shaping minds and
              building futures.
            </p>
            <div className="inline-flex items-center gap-2 bg-blue-900 text-white px-6 py-3 rounded-full text-sm font-semibold" data-id="element-3496">
              <CalendarIcon size={18} className="text-yellow-400" data-id="element-3497" />
              23rd – 29th March, 2026 · Isonyin Grammar School, Isonyin, Ogun
              State
            </div>
            <p className="mt-4 text-sm text-gray-600" data-id="element-3498">
              Hosted by:{' '}
              <strong data-id="element-3499">
                Isonyin Grammar School National Alumni Association
              </strong>{' '}
              &amp; 70th Founder's Day Anniversary Planning Committee, in
              collaboration with School Management &amp; The Isonyin Community
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-16" data-id="element-3500">
            <Card className="bg-blue-50 border-none shadow-md hover:shadow-lg transition-shadow" data-id="element-3501">
              <div className="flex flex-col items-center text-center" data-id="element-3502">
                <img src="https://cdn.magicpatterns.com/uploads/ny9gkWcQrLZfy1eVnzqurw/WhatsApp_Image_2026-03-10_at_19.19.06_(1).jpg" alt="Mr. Olugbenga Odunaike" className="w-20 h-20 rounded-full object-cover border-4 border-blue-900 mb-4 shadow-sm" data-id="element-3503" />
                <h3 className="text-lg font-bold text-blue-900 mb-2" data-id="element-3504">
                  Senior Principal
                </h3>
                <p className="text-gray-700 italic mb-4 text-sm" data-id="element-3505">
                  "This 70th anniversary is a testament to the enduring legacy
                  of our founders and the continuous dedication of our staff,
                  students, and alumni. We look forward to celebrating this
                  monumental milestone together."
                </p>
                <p className="font-semibold text-blue-900 text-sm" data-id="element-3506">
                  - Mr. Olugbenga Odunaike
                </p>
              </div>
            </Card>

            <Card className="bg-blue-50 border-none shadow-md hover:shadow-lg transition-shadow" data-id="element-3507">
              <div className="flex flex-col items-center text-center" data-id="element-3508">
                <img src="https://cdn.magicpatterns.com/uploads/7erE9EWw8YDTq8W8H9WSLg/WhatsApp_Image_2026-03-10_at_19.25.54.jpg" alt="Mrs. Ugwu Theresa" className="w-20 h-20 rounded-full object-cover border-4 border-blue-900 mb-4 shadow-sm" data-id="element-3509" />
                <h3 className="text-lg font-bold text-blue-900 mb-2" data-id="element-3510">
                  Junior Principal
                </h3>
                <p className="text-gray-700 italic mb-4 text-sm" data-id="element-3511">
                  "The Management, Staff and Students of IGS-Junior joyfully
                  rejoice with the Alumni. As the School celebrates this
                  platinum jubilee, may it continue to grow in innovation and
                  moral strength. Congratulations on 70 glorious years of
                  excellence."
                </p>
                <p className="font-semibold text-blue-900 text-sm" data-id="element-3512">
                  - Mrs. Ugwu Theresa
                </p>
              </div>
            </Card>

            <Card className="bg-yellow-50 border-none shadow-md hover:shadow-lg transition-shadow" data-id="element-3513">
              <div className="flex flex-col items-center text-center" data-id="element-3514">
                <div className="w-16 h-16 bg-yellow-500 rounded-full flex items-center justify-center mb-4 text-blue-900" data-id="element-3515">
                  <CalendarIcon size={32} data-id="element-3516" />
                </div>
                <h3 className="text-xl font-bold text-blue-900 mb-2" data-id="element-3517">
                  Planning Committee
                </h3>
                <p className="text-gray-700 italic mb-4" data-id="element-3518">
                  "We have curated a series of events that not only celebrate
                  our past but also lay the groundwork for our future. Join us
                  for a memorable week of reunions, reflections, and rejoicing."
                </p>
                <p className="font-semibold text-blue-900" data-id="element-3519">
                  - 70th Anniversary Committee
                </p>
              </div>
            </Card>

            <Card className="bg-blue-50 border-none shadow-md" data-id="element-3520">
              <div className="flex flex-col items-center text-center" data-id="element-3521">
                <div className="w-16 h-16 bg-blue-900 rounded-full flex items-center justify-center mb-4 text-yellow-400" data-id="element-3522">
                  <GraduationCapIcon size={32} data-id="element-3523" />
                </div>
                <h3 className="text-xl font-bold text-blue-900 mb-2" data-id="element-3524">
                  Old Students Association
                </h3>
                <p className="text-gray-700 italic mb-4" data-id="element-3525">
                  "To all ISOGRAS alumni worldwide, this is our moment to give
                  back to the institution that shaped us. Let us unite to make
                  this Platinum Jubilee a resounding success."
                </p>
                <p className="font-semibold text-blue-900" data-id="element-3526">
                  - National President, ISOGRAS
                </p>
              </div>
            </Card>
          </div>

          <div className="bg-white p-8 rounded-xl shadow-lg border border-gray-100" data-id="element-3527">
            <h3 className="text-2xl font-bold text-blue-900 mb-6 text-center" data-id="element-3528">
              Key Milestones
            </h3>
            <div className="space-y-6" data-id="element-3529">
              <div className="flex" data-id="element-3530">
                <div className="flex flex-col items-center mr-6" data-id="element-3531">
                  <div className="w-4 h-4 bg-yellow-500 rounded-full" data-id="element-3532"></div>
                  <div className="w-0.5 h-full bg-gray-200 my-2" data-id="element-3533"></div>
                </div>
                <div className="pb-6" data-id="element-3534">
                  <h4 className="text-xl font-bold text-blue-900" data-id="element-3535">1956</h4>
                  <p className="text-gray-700" data-id="element-3536">
                    School founded on March 23rd with 23 pioneering students.
                  </p>
                </div>
              </div>
              <div className="flex" data-id="element-3537">
                <div className="flex flex-col items-center mr-6" data-id="element-3538">
                  <div className="w-4 h-4 bg-yellow-500 rounded-full" data-id="element-3539"></div>
                  <div className="w-0.5 h-full bg-gray-200 my-2" data-id="element-3540"></div>
                </div>
                <div className="pb-6" data-id="element-3541">
                  <h4 className="text-xl font-bold text-blue-900" data-id="element-3542">1979</h4>
                  <p className="text-gray-700" data-id="element-3543">
                    Transitioned to a co-educational institution, admitting
                    female students for the first time.
                  </p>
                </div>
              </div>
              <div className="flex" data-id="element-3544">
                <div className="flex flex-col items-center mr-6" data-id="element-3545">
                  <div className="w-4 h-4 bg-yellow-500 rounded-full" data-id="element-3546"></div>
                  <div className="w-0.5 h-full bg-gray-200 my-2" data-id="element-3547"></div>
                </div>
                <div className="pb-6" data-id="element-3548">
                  <h4 className="text-xl font-bold text-blue-900" data-id="element-3549">2006</h4>
                  <p className="text-gray-700" data-id="element-3550">
                    Celebrated Golden Jubilee (50 years) with major
                    infrastructure upgrades funded by alumni.
                  </p>
                </div>
              </div>
              <div className="flex" data-id="element-3551">
                <div className="flex flex-col items-center mr-6" data-id="element-3552">
                  <div className="w-4 h-4 bg-blue-900 rounded-full" data-id="element-3553"></div>
                </div>
                <div data-id="element-3554">
                  <h4 className="text-xl font-bold text-blue-900" data-id="element-3555">2026</h4>
                  <p className="text-gray-700 font-medium" data-id="element-3556">
                    Platinum Jubilee (70 years) Celebration.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Media Coverage Section */}
          <div className="mt-16" data-id="element-3557">
            <h3 className="text-2xl font-bold text-blue-900 mb-2 text-center" data-id="element-3558">
              Media Coverage
            </h3>
            <div className="w-16 h-1 bg-yellow-500 mx-auto mb-8" data-id="element-3559"></div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-3560">
              <a href="https://thenationonlineng.net/isonyin-grammar-school-celebrates-70th-anniversary/" target="_blank" rel="noopener noreferrer" className="bg-white p-6 rounded-xl shadow-md border border-gray-100 hover:shadow-lg hover:border-blue-200 transition-all group flex items-start" data-id="element-3561">
                <div className="bg-blue-50 p-3 rounded-lg mr-4 group-hover:bg-blue-100 transition-colors" data-id="element-3562">
                  <BookOpenIcon className="text-blue-900" size={24} data-id="element-3563" />
                </div>
                <div data-id="element-3564">
                  <h4 className="font-bold text-blue-900 mb-1 group-hover:text-blue-700 transition-colors" data-id="element-3565">
                    The Nation
                  </h4>
                  <p className="text-gray-600 text-sm mb-2" data-id="element-3566">
                    Isonyin Grammar School celebrates 70th anniversary
                  </p>
                  <span className="text-yellow-600 text-xs font-semibold uppercase tracking-wider flex items-center" data-id="element-3567">
                    Read Article{' '}
                    <span className="ml-1 group-hover:translate-x-1 transition-transform" data-id="element-3568">
                      →
                    </span>
                  </span>
                </div>
              </a>

              <a href="https://www.thewhispernews.com/2026/03/isonyin-grammar-school-marks-70-years.html" target="_blank" rel="noopener noreferrer" className="bg-white p-6 rounded-xl shadow-md border border-gray-100 hover:shadow-lg hover:border-blue-200 transition-all group flex items-start" data-id="element-3569">
                <div className="bg-blue-50 p-3 rounded-lg mr-4 group-hover:bg-blue-100 transition-colors" data-id="element-3570">
                  <BookOpenIcon className="text-blue-900" size={24} data-id="element-3571" />
                </div>
                <div data-id="element-3572">
                  <h4 className="font-bold text-blue-900 mb-1 group-hover:text-blue-700 transition-colors" data-id="element-3573">
                    The Whisper News
                  </h4>
                  <p className="text-gray-600 text-sm mb-2" data-id="element-3574">
                    Isonyin Grammar School marks 70 years of impactful legacy
                  </p>
                  <span className="text-yellow-600 text-xs font-semibold uppercase tracking-wider flex items-center" data-id="element-3575">
                    Read Article{' '}
                    <span className="ml-1 group-hover:translate-x-1 transition-transform" data-id="element-3576">
                      →
                    </span>
                  </span>
                </div>
              </a>

              <a href="https://www.todaysupdatenews.com/2026/03/isonyin-grammar-school-marks-70-years.html" target="_blank" rel="noopener noreferrer" className="bg-white p-6 rounded-xl shadow-md border border-gray-100 hover:shadow-lg hover:border-blue-200 transition-all group flex items-start" data-id="element-3577">
                <div className="bg-blue-50 p-3 rounded-lg mr-4 group-hover:bg-blue-100 transition-colors" data-id="element-3578">
                  <BookOpenIcon className="text-blue-900" size={24} data-id="element-3579" />
                </div>
                <div data-id="element-3580">
                  <h4 className="font-bold text-blue-900 mb-1 group-hover:text-blue-700 transition-colors" data-id="element-3581">
                    Today's Update News
                  </h4>
                  <p className="text-gray-600 text-sm mb-2" data-id="element-3582">
                    Isonyin Grammar School marks 70 years of educational
                    excellence
                  </p>
                  <span className="text-yellow-600 text-xs font-semibold uppercase tracking-wider flex items-center" data-id="element-3583">
                    Read Article{' '}
                    <span className="ml-1 group-hover:translate-x-1 transition-transform" data-id="element-3584">
                      →
                    </span>
                  </span>
                </div>
              </a>

              <a href="https://www.infopalavanews.com/2026/03/isonyin-grammar-school-marks-70-years.html" target="_blank" rel="noopener noreferrer" className="bg-white p-6 rounded-xl shadow-md border border-gray-100 hover:shadow-lg hover:border-blue-200 transition-all group flex items-start" data-id="element-3585">
                <div className="bg-blue-50 p-3 rounded-lg mr-4 group-hover:bg-blue-100 transition-colors" data-id="element-3586">
                  <BookOpenIcon className="text-blue-900" size={24} data-id="element-3587" />
                </div>
                <div data-id="element-3588">
                  <h4 className="font-bold text-blue-900 mb-1 group-hover:text-blue-700 transition-colors" data-id="element-3589">
                    Info Palava News
                  </h4>
                  <p className="text-gray-600 text-sm mb-2" data-id="element-3590">
                    Isonyin Grammar School marks 70 years with grand
                    celebrations
                  </p>
                  <span className="text-yellow-600 text-xs font-semibold uppercase tracking-wider flex items-center" data-id="element-3591">
                    Read Article{' '}
                    <span className="ml-1 group-hover:translate-x-1 transition-transform" data-id="element-3592">
                      →
                    </span>
                  </span>
                </div>
              </a>
            </div>
          </div>
        </div>
  }, {
    id: 'programme',
    label: 'Celebration Programme',
    content: <div className="py-8 animate-fadeIn max-w-4xl mx-auto" data-id="element-3593">
          <div className="text-center mb-10" data-id="element-3594">
            <h2 className="text-3xl font-bold text-blue-900 mb-4" data-id="element-3595">
              Programme of Events
            </h2>
            <p className="text-gray-600" data-id="element-3596">
              Join us for a week-long celebration of our 70th Anniversary.
            </p>
          </div>

          {/* Programme of Events Banner */}
          <div className="mb-10 rounded-xl overflow-hidden shadow-lg" data-id="element-3597">
            <img src="https://cdn.magicpatterns.com/uploads/9SCpHLSdj2gFeMaWvjkG7d/Programme_of_events.jpg" alt="Programme of Events — 70th Founder's Day Anniversary Celebration" className="w-full h-auto" data-id="element-3598" />
          </div>

          <Accordion variant="bordered" className="bg-white" data-id="element-3599">
            <AccordionItem title={<span className="text-lg font-bold text-blue-900" data-id="element-3601">
                  Monday 23rd March 2026: Back to School & Press Conference
                </span>} defaultOpen={true} data-id="element-3600">
              <div className="space-y-3 text-gray-700" data-id="element-3602">
                <p className="font-medium text-lg border-b pb-2" data-id="element-3603">
                  Back to School & Press Conference
                </p>
                <div className="flex items-center gap-2" data-id="element-3604">
                  <ClockIcon size={18} className="text-yellow-500" data-id="element-3605" />{' '}
                  <span data-id="element-3606">10:00 AM</span>
                </div>
                <div className="flex items-center gap-2" data-id="element-3607">
                  <MapPinIcon size={18} className="text-yellow-500" data-id="element-3608" />{' '}
                  <span data-id="element-3609">Isonyin Grammar School</span>
                </div>
                <div className="flex items-center gap-2" data-id="element-3610">
                  <UserIcon size={18} className="text-yellow-500" data-id="element-3611" />{' '}
                  <span data-id="element-3612">Press Conference by: National Alumni President</span>
                </div>
                <p className="mt-4 bg-blue-50 p-4 rounded-lg text-sm" data-id="element-3613">
                  Official flag-off of the 70th Anniversary celebrations.
                  Students resume for the anniversary week, followed by a press
                  conference addressed by the National Alumni President.
                </p>
              </div>
            </AccordionItem>

            <AccordionItem title={<span className="text-lg font-bold text-blue-900" data-id="element-3615">
                  Tuesday 24th March 2026: Debate/Quiz & Anniversary Lecture
                </span>} data-id="element-3614">
              <div className="space-y-3 text-gray-700" data-id="element-3616">
                <p className="font-medium text-lg border-b pb-2" data-id="element-3617">
                  Students Debate/Quiz Competition & Anniversary Lecture
                </p>
                <div className="flex items-center gap-2" data-id="element-3618">
                  <ClockIcon size={18} className="text-yellow-500" data-id="element-3619" />{' '}
                  <span data-id="element-3620">10:00 AM — Debate/Quiz Competition</span>
                </div>
                <div className="flex items-center gap-2" data-id="element-3621">
                  <ClockIcon size={18} className="text-yellow-500" data-id="element-3622" />{' '}
                  <span data-id="element-3623">3:00 PM — Anniversary Lecture</span>
                </div>
                <div className="flex items-center gap-2" data-id="element-3624">
                  <UserIcon size={18} className="text-yellow-500" data-id="element-3625" />{' '}
                  <span data-id="element-3626">
                    Lecturer: Mr. R. B. Lawal (Former Director, Lagos Ministry
                    of Education)
                  </span>
                </div>
                <p className="mt-4 bg-blue-50 p-4 rounded-lg text-sm" data-id="element-3627">
                  The morning features an inter-house students debate and quiz
                  competition. In the afternoon, the Anniversary Lecture will be
                  delivered by Mr. R. B. Lawal, a distinguished former Director
                  of the Lagos Ministry of Education.
                </p>
              </div>
            </AccordionItem>

            <AccordionItem title={<span className="text-lg font-bold text-blue-900" data-id="element-3629">
                  Wednesday 25th March 2026: Sporting Activities
                </span>} data-id="element-3628">
              <div className="space-y-3 text-gray-700" data-id="element-3630">
                <p className="font-medium text-lg border-b pb-2" data-id="element-3631">
                  Sporting Activities
                </p>
                <div className="flex items-center gap-2" data-id="element-3632">
                  <ClockIcon size={18} className="text-yellow-500" data-id="element-3633" />{' '}
                  <span data-id="element-3634">10:00 AM</span>
                </div>
                <div className="flex items-center gap-2" data-id="element-3635">
                  <MapPinIcon size={18} className="text-yellow-500" data-id="element-3636" />{' '}
                  <span data-id="element-3637">School Sports Field</span>
                </div>
                <p className="mt-4 bg-blue-50 p-4 rounded-lg text-sm" data-id="element-3638">
                  A full day of sporting activities featuring inter-house
                  competitions, novelty matches between alumni sets, and
                  exhibition games celebrating the school's sporting heritage.
                </p>
              </div>
            </AccordionItem>

            <AccordionItem title={<span className="text-lg font-bold text-blue-900" data-id="element-3640">
                  Thursday 26th March 2026: Prize Giving Day
                </span>} data-id="element-3639">
              <div className="space-y-3 text-gray-700" data-id="element-3641">
                <p className="font-medium text-lg border-b pb-2" data-id="element-3642">
                  Prize Giving Day to Deserving Students
                </p>
                <div className="flex items-center gap-2" data-id="element-3643">
                  <ClockIcon size={18} className="text-yellow-500" data-id="element-3644" />{' '}
                  <span data-id="element-3645">10:00 AM</span>
                </div>
                <div className="flex items-center gap-2" data-id="element-3646">
                  <AwardIcon size={18} className="text-yellow-500" data-id="element-3647" />{' '}
                  <span data-id="element-3648">
                    Awards for academic and extracurricular excellence
                  </span>
                </div>
                <p className="mt-4 bg-blue-50 p-4 rounded-lg text-sm" data-id="element-3649">
                  A special ceremony recognising and rewarding deserving
                  students for outstanding academic performance, character, and
                  contributions to school life.
                </p>
              </div>
            </AccordionItem>

            <AccordionItem title={<span className="text-lg font-bold text-blue-900" data-id="element-3651">
                  Friday 27th March 2026: Road Show & Jumat Service
                </span>} data-id="element-3650">
              <div className="space-y-3 text-gray-700" data-id="element-3652">
                <p className="font-medium text-lg border-b pb-2" data-id="element-3653">
                  Road Show & Jumat Service
                </p>
                <div className="flex items-center gap-2" data-id="element-3654">
                  <ClockIcon size={18} className="text-yellow-500" data-id="element-3655" />{' '}
                  <span data-id="element-3656">
                    9:00 AM – 12:00 Noon — Road Show within Isonyin and environs
                  </span>
                </div>
                <div className="flex items-center gap-2" data-id="element-3657">
                  <ClockIcon size={18} className="text-yellow-500" data-id="element-3658" />{' '}
                  <span data-id="element-3659">1:00 PM — Jumat Service @ Isonyin Central Mosque</span>
                </div>
                <p className="mt-4 bg-blue-50 p-4 rounded-lg text-sm" data-id="element-3660">
                  A colourful road show through Isonyin town and its environs,
                  showcasing the school's 70-year legacy. The day concludes with
                  a special Jumat prayer service at Isonyin Central Mosque.
                </p>
              </div>
            </AccordionItem>

            <AccordionItem title={<span className="text-lg font-bold text-blue-900" data-id="element-3662">
                  Saturday 28th March 2026: Grand Finale & Social
                </span>} data-id="element-3661">
              <div className="space-y-3 text-gray-700" data-id="element-3663">
                <p className="font-medium text-lg border-b pb-2" data-id="element-3664">
                  Commissioning of Set's Projects, Merit Awards & Social
                </p>
                <div className="flex items-center gap-2" data-id="element-3665">
                  <BuildingIcon size={18} className="text-yellow-500" data-id="element-3666" />{' '}
                  <span data-id="element-3667">Commissioning of Set's Projects</span>
                </div>
                <div className="flex items-center gap-2" data-id="element-3668">
                  <AwardIcon size={18} className="text-yellow-500" data-id="element-3669" />{' '}
                  <span data-id="element-3670">Merit Awards Ceremony</span>
                </div>
                <div className="mt-4 bg-yellow-50 p-4 rounded-lg border border-yellow-200" data-id="element-3671">
                  <p className="font-bold text-blue-900 mb-2" data-id="element-3672">SOCIAL</p>
                  <ul className="space-y-1 text-sm" data-id="element-3673">
                    <li data-id="element-3674">
                      🎨 <strong data-id="element-3675">Outfits:</strong> All shades of blue being the
                      school colour
                    </li>
                    <li data-id="element-3676">
                      🎵 <strong data-id="element-3677">Entertainment:</strong> Menu, Life Band and
                      Cultural Troupe
                    </li>
                  </ul>
                </div>
                <p className="mt-4 bg-blue-50 p-4 rounded-lg text-sm" data-id="element-3678">
                  The grand climax of the celebrations. Alumni sets commission
                  their donated projects, followed by merit awards to
                  distinguished alumni and outstanding staff. The evening
                  features a grand social with live band, cultural troupe
                  performances, and fine dining.
                </p>
              </div>
            </AccordionItem>

            <AccordionItem title={<span className="text-lg font-bold text-blue-900" data-id="element-3680">
                  Sunday 29th March 2026: Thanksgiving Service
                </span>} data-id="element-3679">
              <div className="space-y-3 text-gray-700" data-id="element-3681">
                <p className="font-medium text-lg border-b pb-2" data-id="element-3682">
                  Church Service @ Anglican Church Isonyin
                </p>
                <div className="flex items-center gap-2" data-id="element-3683">
                  <ClockIcon size={18} className="text-yellow-500" data-id="element-3684" />{' '}
                  <span data-id="element-3685">10:00 AM</span>
                </div>
                <div className="flex items-center gap-2" data-id="element-3686">
                  <MapPinIcon size={18} className="text-yellow-500" data-id="element-3687" />{' '}
                  <span data-id="element-3688">Anglican Church, Isonyin</span>
                </div>
                <div className="mt-4 bg-yellow-50 p-4 rounded-lg border border-yellow-200 text-sm" data-id="element-3689">
                  <strong data-id="element-3690">Colour Code:</strong> All shades of Blue and White for
                  the Thanksgiving Service.
                </div>
                <p className="mt-4 bg-blue-50 p-4 rounded-lg text-sm" data-id="element-3691">
                  A special thanksgiving service to appreciate God for 70 years
                  of His faithfulness, guidance, and the continuous growth of
                  Isonyin Grammar School.
                </p>
              </div>
            </AccordionItem>
          </Accordion>

          {/* Enquiries */}
          <div className="mt-8 bg-blue-900 text-white p-6 rounded-xl text-center" data-id="element-3692">
            <p className="text-lg font-semibold mb-2" data-id="element-3693">Enquiries</p>
            <p className="text-blue-200" data-id="element-3694">
              National Publicity Sec., Yeye Adesola Salami —{' '}
              <a href="tel:08132307631" className="text-yellow-400 font-bold hover:underline" data-id="element-3695">
                08132307631
              </a>
            </p>
          </div>
        </div>
  }, {
    id: 'register',
    label: 'Alumni Registration',
    content: <div className="py-8 animate-fadeIn max-w-3xl mx-auto" data-id="element-3696">
          <div className="text-center mb-8" data-id="element-3697">
            <h2 className="text-3xl font-bold text-blue-900 mb-4" data-id="element-3698">
              Anniversary Registration
            </h2>
            <p className="text-gray-600" data-id="element-3699">
              Register to attend the 70th Anniversary events and reconnect with
              your alma mater.
            </p>
          </div>

          {formStatus === 'success' ? <div className="bg-green-50 border border-green-200 rounded-xl p-8 text-center" data-id="element-3700">
              <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6" data-id="element-3701">
                <CheckCircleIcon size={40} className="text-green-600" data-id="element-3702" />
              </div>
              <h3 className="text-2xl font-bold text-green-800 mb-2" data-id="element-3703">
                Registration Successful!
              </h3>
              <p className="text-green-700 mb-6" data-id="element-3704">
                Thank you for registering for the 70th Anniversary. We have sent
                a confirmation email with further details.
              </p>
              <Button onClick={() => setFormStatus('idle')} variant="outline" className="border-green-600 text-green-700 hover:bg-green-600 hover:text-white" data-id="element-3705">
                Register Another Alumnus
              </Button>
            </div> : <form onSubmit={handleRegistrationSubmit} className="bg-white p-8 rounded-xl shadow-lg border border-gray-100" data-id="element-3706">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6" data-id="element-3707">
                <div data-id="element-3708">
                  <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-3709">
                    Full Name
                  </label>
                  <div className="relative" data-id="element-3710">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-3711">
                      <UserIcon size={18} className="text-gray-400" data-id="element-3712" />
                    </div>
                    <input required type="text" className="pl-10 w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all" placeholder="John Doe" data-id="element-3713" />
                  </div>
                </div>
                <div data-id="element-3714">
                  <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-3715">
                    Graduation Year / Set
                  </label>
                  <div className="relative" data-id="element-3716">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-3717">
                      <GraduationCapIcon size={18} className="text-gray-400" data-id="element-3718" />
                    </div>
                    <input required type="text" className="pl-10 w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all" placeholder="e.g. 1985" data-id="element-3719" />
                  </div>
                </div>
                <div data-id="element-3720">
                  <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-3721">
                    Email Address
                  </label>
                  <div className="relative" data-id="element-3722">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-3723">
                      <MailIcon size={18} className="text-gray-400" data-id="element-3724" />
                    </div>
                    <input required type="email" className="pl-10 w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all" placeholder="john@example.com" data-id="element-3725" />
                  </div>
                </div>
                <div data-id="element-3726">
                  <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-3727">
                    Phone Number
                  </label>
                  <div className="relative" data-id="element-3728">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-3729">
                      <PhoneIcon size={18} className="text-gray-400" data-id="element-3730" />
                    </div>
                    <input required type="tel" className="pl-10 w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all" placeholder="+234..." data-id="element-3731" />
                  </div>
                </div>
                <div data-id="element-3732">
                  <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-3733">
                    Current Location
                  </label>
                  <div className="relative" data-id="element-3734">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-3735">
                      <MapPinIcon size={18} className="text-gray-400" data-id="element-3736" />
                    </div>
                    <input required type="text" className="pl-10 w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all" placeholder="City, Country" data-id="element-3737" />
                  </div>
                </div>
                <div data-id="element-3738">
                  <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-3739">
                    Profession
                  </label>
                  <div className="relative" data-id="element-3740">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-3741">
                      <BriefcaseIcon size={18} className="text-gray-400" data-id="element-3742" />
                    </div>
                    <input required type="text" className="pl-10 w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all" placeholder="e.g. Engineer, Doctor" data-id="element-3743" />
                  </div>
                </div>
              </div>

              <div className="mb-8" data-id="element-3744">
                <label className="block text-sm font-medium text-gray-700 mb-3" data-id="element-3745">
                  How would you like to support the anniversary?
                </label>
                <div className="space-y-2" data-id="element-3746">
                  <label className="flex items-center space-x-3 cursor-pointer" data-id="element-3747">
                    <input type="checkbox" className="w-5 h-5 text-blue-600 rounded border-gray-300 focus:ring-blue-500" data-id="element-3748" />
                    <span className="text-gray-700" data-id="element-3749">Financial Donation</span>
                  </label>
                  <label className="flex items-center space-x-3 cursor-pointer" data-id="element-3750">
                    <input type="checkbox" className="w-5 h-5 text-blue-600 rounded border-gray-300 focus:ring-blue-500" data-id="element-3751" />
                    <span className="text-gray-700" data-id="element-3752">Event Sponsorship</span>
                  </label>
                  <label className="flex items-center space-x-3 cursor-pointer" data-id="element-3753">
                    <input type="checkbox" className="w-5 h-5 text-blue-600 rounded border-gray-300 focus:ring-blue-500" data-id="element-3754" />
                    <span className="text-gray-700" data-id="element-3755">
                      Volunteer / Committee Member
                    </span>
                  </label>
                  <label className="flex items-center space-x-3 cursor-pointer" data-id="element-3756">
                    <input type="checkbox" className="w-5 h-5 text-blue-600 rounded border-gray-300 focus:ring-blue-500" data-id="element-3757" />
                    <span className="text-gray-700" data-id="element-3758">
                      Just attending the events
                    </span>
                  </label>
                </div>
              </div>

              <Button type="submit" variant="primary" className="w-full py-4 text-lg" disabled={formStatus === 'submitting'} data-id="element-3759">
                {formStatus === 'submitting' ? 'Submitting...' : 'Register Now'}
              </Button>
            </form>}
        </div>
  }, {
    id: 'support',
    label: 'Support & Donations',
    content: <div className="py-8 animate-fadeIn" data-id="element-3760">
          <div className="text-center mb-12" data-id="element-3761">
            <h2 className="text-3xl font-bold text-blue-900 mb-4" data-id="element-3762">
              Support the 70th Anniversary
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto" data-id="element-3763">
              Your contributions will help make this milestone celebration a
              success and leave a lasting legacy for future generations of IGS
              students.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12" data-id="element-3764">
            <Card hover className="flex flex-col h-full border-t-4 border-t-blue-900" data-id="element-3765">
              <div className="p-4 flex-grow flex flex-col items-center text-center" data-id="element-3766">
                <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mb-4 text-blue-900" data-id="element-3767">
                  <BuildingIcon size={32} data-id="element-3768" />
                </div>
                <h3 className="text-xl font-bold text-blue-900 mb-2" data-id="element-3769">
                  Infrastructure
                </h3>
                <p className="text-gray-600 text-sm mb-6 flex-grow" data-id="element-3770">
                  Support the renovation of classrooms, laboratories, and
                  boarding facilities as part of our legacy projects.
                </p>
                <Button variant="primary" className="w-full" data-id="element-3771">
                  Donate
                </Button>
              </div>
            </Card>

            <Card hover className="flex flex-col h-full border-t-4 border-t-yellow-500" data-id="element-3772">
              <div className="p-4 flex-grow flex flex-col items-center text-center" data-id="element-3773">
                <div className="w-16 h-16 bg-yellow-100 rounded-full flex items-center justify-center mb-4 text-yellow-600" data-id="element-3774">
                  <BookOpenIcon size={32} data-id="element-3775" />
                </div>
                <h3 className="text-xl font-bold text-blue-900 mb-2" data-id="element-3776">
                  Scholarship Fund
                </h3>
                <p className="text-gray-600 text-sm mb-6 flex-grow" data-id="element-3777">
                  Help indigent but brilliant students access quality education
                  at IGS through our 70th Anniversary Scholarship Fund.
                </p>
                <Button variant="primary" className="w-full" data-id="element-3778">
                  Donate
                </Button>
              </div>
            </Card>

            <Card hover className="flex flex-col h-full border-t-4 border-t-blue-500" data-id="element-3779">
              <div className="p-4 flex-grow flex flex-col items-center text-center" data-id="element-3780">
                <div className="w-16 h-16 bg-blue-50 rounded-full flex items-center justify-center mb-4 text-blue-500" data-id="element-3781">
                  <AwardIcon size={32} data-id="element-3782" />
                </div>
                <h3 className="text-xl font-bold text-blue-900 mb-2" data-id="element-3783">
                  Event Sponsorship
                </h3>
                <p className="text-gray-600 text-sm mb-6 flex-grow" data-id="element-3784">
                  Sponsor specific anniversary events like the Lecture, Sports
                  Competition, or the Grand Reception.
                </p>
                <Button variant="primary" className="w-full" data-id="element-3785">
                  Sponsor
                </Button>
              </div>
            </Card>

            <Card hover className="flex flex-col h-full border-t-4 border-t-red-500" data-id="element-3786">
              <div className="p-4 flex-grow flex flex-col items-center text-center" data-id="element-3787">
                <div className="w-16 h-16 bg-red-50 rounded-full flex items-center justify-center mb-4 text-red-500" data-id="element-3788">
                  <HeartIcon size={32} data-id="element-3789" />
                </div>
                <h3 className="text-xl font-bold text-blue-900 mb-2" data-id="element-3790">
                  General Support
                </h3>
                <p className="text-gray-600 text-sm mb-6 flex-grow" data-id="element-3791">
                  Make a general donation towards the logistics and planning of
                  the 70th Anniversary celebrations.
                </p>
                <Button variant="primary" className="w-full" data-id="element-3792">
                  Donate
                </Button>
              </div>
            </Card>
          </div>

          <div className="bg-gradient-to-r from-blue-900 via-blue-800 to-blue-900 p-8 md:p-10 rounded-xl text-center max-w-3xl mx-auto" data-id="element-3793">
            <HeartIcon size={40} className="text-yellow-400 mx-auto mb-4" data-id="element-3794" />
            <h3 className="text-2xl font-bold text-white mb-3" data-id="element-3795">
              Ready to Make a Difference?
            </h3>
            <p className="text-blue-100 mb-6 max-w-xl mx-auto leading-relaxed" data-id="element-3796">
              Every contribution, big or small, helps us create a memorable
              celebration and build a lasting legacy for future generations of
              IGS students.
            </p>
            <div className="flex flex-wrap justify-center gap-4" data-id="element-3797">
              <Button href="/contact" variant="primary" className="bg-yellow-500 text-blue-950 hover:bg-yellow-400 border-none shadow-lg" data-id="element-3798">
                Contact Us to Donate
              </Button>
              <Button href="/support" variant="outline" className="border-white text-white hover:bg-white/10" data-id="element-3799">
                Other Ways to Support
              </Button>
            </div>
          </div>
        </div>
  }, {
    id: 'alumni',
    label: 'Distinguished Alumni',
    content: <div className="py-8 animate-fadeIn" data-id="element-3800">
          <div className="text-center mb-12" data-id="element-3801">
            <h2 className="text-3xl font-bold text-blue-900 mb-4" data-id="element-3802">
              Celebrating Our Icons
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto" data-id="element-3803">
              Over 70 years, IGS has produced outstanding individuals who have
              made significant impacts across various sectors globally.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8" data-id="element-3804">
            <Card hover padding="none" className="overflow-hidden" data-id="element-3805">
              <div className="h-64 bg-gray-200 relative" data-id="element-3806">
                <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/jn44YtV68i3KkaTAFc7QtH/otubu.png" alt="Prof. Akintunde Otubu" className="w-full h-full object-cover object-top" data-id="element-3807" />
                <div className="absolute bottom-0 left-0 w-full bg-gradient-to-t from-blue-900 to-transparent p-4 pt-12" data-id="element-3808">
                  <span className="bg-yellow-500 text-blue-900 text-xs font-bold px-2 py-1 rounded" data-id="element-3809">
                    Class of 1991
                  </span>
                </div>
              </div>
              <div className="p-6" data-id="element-3810">
                <h3 className="text-xl font-bold text-blue-900 mb-1" data-id="element-3811">
                  Prof. Akintunde Otubu
                </h3>
                <p className="text-blue-600 font-medium mb-4 text-sm" data-id="element-3812">
                  Law Lecturer, University of Lagos
                </p>
                <p className="text-gray-600 text-sm" data-id="element-3813">
                  Distinguished legal scholar specializing in Land, Housing,
                  Planning, and Environment Law. Current National President of
                  the Alumni Association.
                </p>
              </div>
            </Card>

            <Card hover padding="none" className="overflow-hidden" data-id="element-3814">
              <div className="h-64 bg-gray-200 relative" data-id="element-3815">
                <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/3Hew6rxsK47JVpej3qfHsv/WhatsApp_Image_2025-11-10_at_20.52.09.jpg" alt="Elder Lanre Oyesile" className="w-full h-full object-cover object-top" data-id="element-3816" />
                <div className="absolute bottom-0 left-0 w-full bg-gradient-to-t from-blue-900 to-transparent p-4 pt-12" data-id="element-3817">
                  <span className="bg-yellow-500 text-blue-900 text-xs font-bold px-2 py-1 rounded" data-id="element-3818">
                    Class of 1981
                  </span>
                </div>
              </div>
              <div className="p-6" data-id="element-3819">
                <h3 className="text-xl font-bold text-blue-900 mb-1" data-id="element-3820">
                  Elder Lanre Oyesile
                </h3>
                <p className="text-blue-600 font-medium mb-4 text-sm" data-id="element-3821">
                  Entrepreneur & Properties Consultant
                </p>
                <p className="text-gray-600 text-sm" data-id="element-3822">
                  Seasoned professional with a distinguished career in public
                  service and entrepreneurship. MD/CEO of Larry Jay Properties
                  Consultant.
                </p>
              </div>
            </Card>

            <Card hover padding="none" className="overflow-hidden" data-id="element-3823">
              <div className="h-64 bg-gray-200 relative flex items-center justify-center bg-blue-50" data-id="element-3824">
                <UserIcon size={64} className="text-blue-200" data-id="element-3825" />
                <div className="absolute bottom-0 left-0 w-full bg-gradient-to-t from-blue-900 to-transparent p-4 pt-12" data-id="element-3826">
                  <span className="bg-yellow-500 text-blue-900 text-xs font-bold px-2 py-1 rounded" data-id="element-3827">
                    Class of 1977
                  </span>
                </div>
              </div>
              <div className="p-6" data-id="element-3828">
                <h3 className="text-xl font-bold text-blue-900 mb-1" data-id="element-3829">
                  Oludolapo Daniel Odulana
                </h3>
                <p className="text-blue-600 font-medium mb-4 text-sm" data-id="element-3830">
                  Former Permanent Secretary
                </p>
                <p className="text-gray-600 text-sm" data-id="element-3831">
                  Set a remarkable benchmark as a Permanent Secretary in Ogun
                  State. A proud member of the Golden Gateway Set (1973-1977).
                </p>
              </div>
            </Card>
          </div>
        </div>
  }, {
    id: 'livestream',
    label: '📺 Live Stream',
    content: <div className="py-8 animate-fadeIn" data-id="element-3832">
          <div className="text-center mb-10" data-id="element-3833">
            <h2 className="text-3xl font-bold text-blue-900 mb-4" data-id="element-3834">
              Watch Anniversary Events Live
            </h2>
            <div className="w-24 h-1 bg-yellow-500 mx-auto mb-6" data-id="element-3835"></div>
            <p className="text-gray-600 max-w-2xl mx-auto" data-id="element-3836">
              Stream all 70th Anniversary activities directly from our YouTube
              channel. Don't miss a moment of the celebration!
            </p>
          </div>

          {/* Featured Live Stream */}
          <div className="max-w-4xl mx-auto mb-12" data-id="element-3837">
            <div className="bg-white rounded-2xl shadow-xl overflow-hidden border border-gray-100" data-id="element-3838">
              <div className="relative" data-id="element-3839">
                <div className="aspect-video bg-gray-900 relative" data-id="element-3840">
                  <iframe className="w-full h-full absolute inset-0" src="https://www.youtube.com/embed/live_stream?channel=UCIsonyinGrammarSchool" title="IGS 70th Anniversary Live Stream" frameBorder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" allowFullScreen data-id="element-3841"></iframe>
                  {/* Fallback overlay when no live stream is active */}
                  <div className="absolute inset-0 bg-gradient-to-br from-blue-950 via-blue-900 to-blue-950 flex flex-col items-center justify-center text-white pointer-events-none" id="stream-fallback" data-id="element-3842">
                    <div className="w-20 h-20 bg-red-600 rounded-full flex items-center justify-center mb-6 animate-pulse-slow" data-id="element-3843">
                      <VideoIcon size={40} data-id="element-3844" />
                    </div>
                    <h3 className="text-2xl font-bold mb-2" data-id="element-3845">
                      Live Stream Coming Soon
                    </h3>
                    <p className="text-blue-200 text-center max-w-md px-4" data-id="element-3846">
                      The live broadcast will begin during the anniversary
                      events on March 23–29, 2026. Stay tuned!
                    </p>
                    <div className="mt-6 flex items-center gap-2 bg-red-600/20 border border-red-500/30 rounded-full px-4 py-2" data-id="element-3847">
                      <span className="w-3 h-3 bg-red-500 rounded-full animate-pulse" data-id="element-3848"></span>
                      <span className="text-sm font-medium text-red-300" data-id="element-3849">
                        Awaiting Live Broadcast
                      </span>
                    </div>
                  </div>
                </div>
              </div>
              <div className="p-6" data-id="element-3850">
                <div className="flex items-center justify-between flex-wrap gap-4" data-id="element-3851">
                  <div data-id="element-3852">
                    <h3 className="text-xl font-bold text-blue-900" data-id="element-3853">
                      IGS 70th Anniversary — Main Stream
                    </h3>
                    <p className="text-gray-500 text-sm mt-1" data-id="element-3854">
                      Isonyin Grammar School Official Channel
                    </p>
                  </div>
                  <a href="https://www.youtube.com/@IsonyinGrammarSchool" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 bg-red-600 hover:bg-red-700 text-white font-medium px-5 py-2.5 rounded-lg transition-colors shadow-md" data-id="element-3855">
                    <VideoIcon size={18} data-id="element-3856" />
                    Watch on YouTube
                  </a>
                </div>
              </div>
            </div>
          </div>

          {/* Event-Specific Streams */}
          <div className="max-w-5xl mx-auto" data-id="element-3857">
            <h3 className="text-2xl font-bold text-blue-900 mb-6 text-center" data-id="element-3858">
              Event Recordings & Streams
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-3859">
              <div className="bg-white rounded-xl shadow-md overflow-hidden border border-gray-100" data-id="element-3860">
                <div className="aspect-video bg-gray-900 relative" data-id="element-3861">
                  <iframe className="w-full h-full absolute inset-0" src="https://www.youtube.com/embed/videoseries?list=PLIsonyinGrammarSchool70th" title="Opening Ceremony" frameBorder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen data-id="element-3862"></iframe>
                </div>
                <div className="p-4" data-id="element-3863">
                  <div className="flex items-center gap-2 mb-2" data-id="element-3864">
                    <span className="bg-blue-100 text-blue-800 text-xs font-semibold px-2.5 py-0.5 rounded" data-id="element-3865">
                      March 23
                    </span>
                  </div>
                  <h4 className="font-bold text-blue-900" data-id="element-3866">
                    Opening Ceremony & Press Briefing
                  </h4>
                  <p className="text-gray-500 text-sm mt-1" data-id="element-3867">
                    Official flag-off of the 70th Anniversary celebrations
                  </p>
                </div>
              </div>

              <div className="bg-white rounded-xl shadow-md overflow-hidden border border-gray-100" data-id="element-3868">
                <div className="aspect-video bg-gray-900 relative" data-id="element-3869">
                  <iframe className="w-full h-full absolute inset-0" src="https://www.youtube.com/embed/videoseries?list=PLIsonyinGrammarSchool70th" title="Anniversary Lecture" frameBorder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen data-id="element-3870"></iframe>
                </div>
                <div className="p-4" data-id="element-3871">
                  <div className="flex items-center gap-2 mb-2" data-id="element-3872">
                    <span className="bg-blue-100 text-blue-800 text-xs font-semibold px-2.5 py-0.5 rounded" data-id="element-3873">
                      March 27
                    </span>
                  </div>
                  <h4 className="font-bold text-blue-900" data-id="element-3874">
                    Anniversary Lecture & Alumni Networking
                  </h4>
                  <p className="text-gray-500 text-sm mt-1" data-id="element-3875">
                    Keynote lecture and alumni networking sessions
                  </p>
                </div>
              </div>

              <div className="bg-white rounded-xl shadow-md overflow-hidden border border-gray-100" data-id="element-3876">
                <div className="aspect-video bg-gray-900 relative" data-id="element-3877">
                  <iframe className="w-full h-full absolute inset-0" src="https://www.youtube.com/embed/videoseries?list=PLIsonyinGrammarSchool70th" title="Grand Celebration" frameBorder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen data-id="element-3878"></iframe>
                </div>
                <div className="p-4" data-id="element-3879">
                  <div className="flex items-center gap-2 mb-2" data-id="element-3880">
                    <span className="bg-yellow-100 text-yellow-800 text-xs font-semibold px-2.5 py-0.5 rounded" data-id="element-3881">
                      March 28
                    </span>
                    <span className="bg-red-100 text-red-700 text-xs font-semibold px-2.5 py-0.5 rounded" data-id="element-3882">
                      Main Event
                    </span>
                  </div>
                  <h4 className="font-bold text-blue-900" data-id="element-3883">
                    Grand Anniversary Celebration
                  </h4>
                  <p className="text-gray-500 text-sm mt-1" data-id="element-3884">
                    Cultural displays, project commissioning, awards & banquet
                  </p>
                </div>
              </div>

              <div className="bg-white rounded-xl shadow-md overflow-hidden border border-gray-100" data-id="element-3885">
                <div className="aspect-video bg-gray-900 relative" data-id="element-3886">
                  <iframe className="w-full h-full absolute inset-0" src="https://www.youtube.com/embed/videoseries?list=PLIsonyinGrammarSchool70th" title="Thanksgiving Service" frameBorder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen data-id="element-3887"></iframe>
                </div>
                <div className="p-4" data-id="element-3888">
                  <div className="flex items-center gap-2 mb-2" data-id="element-3889">
                    <span className="bg-blue-100 text-blue-800 text-xs font-semibold px-2.5 py-0.5 rounded" data-id="element-3890">
                      March 29
                    </span>
                  </div>
                  <h4 className="font-bold text-blue-900" data-id="element-3891">
                    Thanksgiving Service
                  </h4>
                  <p className="text-gray-500 text-sm mt-1" data-id="element-3892">
                    Special thanksgiving at Emmanuel Anglican Church, Isonyin
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Subscribe CTA */}
          <div className="mt-12 bg-gradient-to-r from-red-600 to-red-700 rounded-xl p-8 text-center max-w-3xl mx-auto" data-id="element-3893">
            <VideoIcon size={36} className="text-white mx-auto mb-4" data-id="element-3894" />
            <h3 className="text-2xl font-bold text-white mb-3" data-id="element-3895">
              Never Miss a Moment
            </h3>
            <p className="text-red-100 mb-6 max-w-xl mx-auto" data-id="element-3896">
              Subscribe to our YouTube channel and turn on notifications to get
              alerts when we go live during the anniversary week.
            </p>
            <a href="https://www.youtube.com/@IsonyinGrammarSchool?sub_confirmation=1" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 bg-white text-red-600 font-bold px-8 py-3 rounded-lg hover:bg-red-50 transition-colors shadow-lg" data-id="element-3897">
              <VideoIcon size={20} data-id="element-3898" />
              Subscribe on YouTube
            </a>
          </div>
        </div>
  }];
  return <div className="min-h-screen bg-gray-50" data-id="element-3899">
      <PageHeader title="70th Anniversary Celebration" description="Join us as we celebrate seven decades of academic excellence, leadership, and legacy." backgroundImage="https://uploadthingy.s3.us-west-1.amazonaws.com/de3EQh38fKgVchW9xJS4du/WhatsApp_Image_2025-10-24_at_10.01.26.jpg" breadcrumbs={[{
      label: 'Home',
      path: '/'
    }, {
      label: '70th Anniversary',
      path: '/anniversary'
    }]} data-id="element-3900" />

      <div className="container mx-auto px-4 py-12" data-id="element-3901">
        <Tabs tabs={tabs} variant="underline" fullWidth className="max-w-6xl mx-auto" data-id="element-3902" />
      </div>
    </div>;
};
export default Anniversary;